/**
 * Projects Metrics and Calculation Logic
 *
 * Defines completion calculation, status metrics, category determination,
 * and data availability checks for Projects.
 */

// Get project completion metrics
export const getProjectMetrics = (project) => {
  if (!project) return { completion: 0, completedFields: 0, totalFields: 0 };

  const requiredFields = [
    "project_name",
    "project_type",
    "status",
    "location",
    "budget",
    "start_date",
  ];

  const optionalFields = [
    "description",
    "end_date",
    "total_area",
    "units",
    "planning_permission",
    "architect",
    "contractor",
  ];

  const allFields = [...requiredFields, ...optionalFields];

  const completedFields = allFields.filter((field) => {
    const value = project[field];
    return value !== null && value !== undefined && value !== "";
  }).length;

  const completion = Math.round((completedFields / allFields.length) * 100);

  return {
    completion,
    completedFields,
    totalFields: allFields.length,
    requiredFields: requiredFields.length,
    completedRequiredFields: requiredFields.filter((field) => {
      const value = project[field];
      return value !== null && value !== undefined && value !== "";
    }).length,
  };
};

// Calculate overall metrics for a list of projects
export const calculateProjectsMetrics = (projects = []) => {
  const total = projects.length;

  const statusCounts = projects.reduce((acc, project) => {
    const status = project.status || "planning";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const typeCounts = projects.reduce((acc, project) => {
    const type = project.project_type || "residential";
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  // Calculate average completion
  const totalCompletion = projects.reduce((sum, project) => {
    return sum + getProjectMetrics(project).completion;
  }, 0);

  const averageCompletion = total > 0 ? Math.round(totalCompletion / total) : 0;

  // Calculate data availability
  const dataAvailability =
    (projects.reduce((sum, project) => {
      return sum + getProjectMetrics(project).completion;
    }, 0) /
      (total * 100)) *
    100;

  return {
    total,
    active: statusCounts.active || 0,
    planning: statusCounts.planning || 0,
    completed: statusCounts.completed || 0,
    onHold: statusCounts["on-hold"] || 0,
    cancelled: statusCounts.cancelled || 0,

    // Type breakdown
    residential: typeCounts.residential || 0,
    commercial: typeCounts.commercial || 0,
    mixedUse: typeCounts["mixed-use"] || 0,
    industrial: typeCounts.industrial || 0,
    land: typeCounts.land || 0,

    // Completion metrics
    averageCompletion: Math.round(averageCompletion),
    dataAvailability: Math.round(dataAvailability),

    // Status percentages
    activePercentage:
      total > 0 ? Math.round(((statusCounts.active || 0) / total) * 100) : 0,
    completedPercentage:
      total > 0 ? Math.round(((statusCounts.completed || 0) / total) * 100) : 0,
  };
};

// Determine project category for icons
export const getProjectCategory = (project) => {
  if (!project.project_type) return "residential";

  const type = project.project_type.toLowerCase();

  // Map project types to categories
  const categoryMap = {
    residential: "residential",
    commercial: "commercial",
    "mixed-use": "mixed-use",
    industrial: "industrial",
    land: "land",
    office: "commercial",
    retail: "commercial",
    warehouse: "industrial",
    apartment: "residential",
    house: "residential",
  };

  return categoryMap[type] || "residential";
};

// Check data availability for a project
export const checkProjectDataAvailability = (project) => {
  const metrics = getProjectMetrics(project);

  return {
    hasBasicInfo: !!(project.project_name && project.project_type),
    hasLocation: !!project.location,
    hasFinancials: !!project.budget,
    hasTimeline: !!project.start_date,
    hasDescription: !!project.description,
    completionLevel: metrics.completion,

    // Quality indicators
    isComplete: metrics.completion === 100,
    isWellFormed: metrics.completion >= 80,
    needsAttention: metrics.completion < 50,

    // Missing critical data
    missingCritical: metrics.completedRequiredFields < metrics.requiredFields,
  };
};

// Generate status summary for projects
export const getProjectsStatusSummary = (projects = []) => {
  const metrics = calculateProjectsMetrics(projects);

  return [
    {
      label: "Total Projects",
      value: metrics.total,
      type: "count",
      color: "blue",
    },
    {
      label: "Active",
      value: metrics.active,
      percentage: metrics.activePercentage,
      type: "status",
      color: "green",
    },
    {
      label: "Completed",
      value: metrics.completed,
      percentage: metrics.completedPercentage,
      type: "status",
      color: "gray",
    },
    {
      label: "Data Completeness",
      value: `${metrics.averageCompletion}%`,
      type: "percentage",
      color:
        metrics.averageCompletion >= 80
          ? "green"
          : metrics.averageCompletion >= 60
            ? "yellow"
            : "red",
    },
  ];
};
